<template>
  <div class="timber-statistics">
    <div class="statistics-header">
      <div class="statistics-header__left">
        <img src="/img/developer/timber.svg" width="24" height="24" alt="Timber" />
        <h4>Timber</h4>
      </div>
      <div class="period-tabs">
        <button
          v-for="days in periods"
          :key="days"
          @click="changePeriod(days)"
          :class="['period-tab', { active: selectedDays === days }]"
        >
          {{ days === 1 ? '24h' : days + 'd' }}
        </button>
      </div>
    </div>

    <div class="kpi-cards">
      <div class="kpi-card">
        <div class="kpi-icon kpi-icon--blue">
          <i class="fa fa-stream"></i>
        </div>
        <div class="kpi-details">
          <div class="kpi-label">Total Events</div>
          <div class="kpi-value">{{ totalEvents }}</div>
        </div>
      </div>

      <div class="kpi-card kpi-card--error">
        <div class="kpi-icon kpi-icon--red">
          <i class="fa fa-exclamation-circle"></i>
        </div>
        <div class="kpi-details">
          <div class="kpi-label">Errors</div>
          <div class="kpi-value">{{ errorCount }}</div>
          <div class="kpi-sub" v-if="totalEvents">{{ errorRate }}% of total</div>
        </div>
      </div>

      <div class="kpi-card kpi-card--warn">
        <div class="kpi-icon kpi-icon--yellow">
          <i class="fa fa-exclamation-triangle"></i>
        </div>
        <div class="kpi-details">
          <div class="kpi-label">Warnings</div>
          <div class="kpi-value">{{ warnCount }}</div>
          <div class="kpi-sub" v-if="totalEvents">{{ warnRate }}% of total</div>
        </div>
      </div>

      <div class="kpi-card">
        <div class="kpi-icon kpi-icon--teal">
          <i class="fa fa-database"></i>
        </div>
        <div class="kpi-details">
          <div class="kpi-label">Ingested</div>
          <div class="kpi-value">{{ formatBytes(usage.bytes_ingested || 0) }}</div>
          <div class="kpi-sub">this month</div>
        </div>
      </div>
    </div>

    <div class="timber-chart" v-if="timelineBuckets.length > 0">
      <div class="timber-chart__header">
        <h5>Event Volume</h5>
        <div class="timber-chart__legend">
          <span class="legend-item"><span class="legend-dot legend-dot--info"></span>Info</span>
          <span class="legend-item"><span class="legend-dot legend-dot--warn"></span>Warn</span>
          <span class="legend-item"><span class="legend-dot legend-dot--error"></span>Error</span>
          <span class="legend-item"><span class="legend-dot legend-dot--debug"></span>Debug</span>
        </div>
      </div>
      <div class="timber-timeline">
        <div class="timber-timeline__y-axis">
          <div class="timber-timeline__y-label">{{ timelineMax }}</div>
          <div class="timber-timeline__y-label">{{ Math.round(timelineMax * 0.5) }}</div>
          <div class="timber-timeline__y-label">0</div>
        </div>
        <div class="timber-timeline__area">
          <div class="timber-timeline__grid">
            <div class="timber-timeline__grid-line" v-for="i in 3" :key="i"></div>
          </div>
          <div class="timber-timeline__bars">
            <div
              v-for="(bucket, idx) in timelineBuckets"
              :key="idx"
              class="timber-timeline__col"
              @mouseenter="showTooltip(bucket, $event)"
              @mousemove="moveTooltip($event)"
              @mouseleave="hideTooltip"
            >
              <div class="timber-timeline__stack">
                <div v-if="bucket.error" class="timber-timeline__bar timber-timeline__bar--error" :style="{ height: barHeight(bucket.error) }"></div>
                <div v-if="bucket.warn" class="timber-timeline__bar timber-timeline__bar--warn" :style="{ height: barHeight(bucket.warn) }"></div>
                <div v-if="bucket.info" class="timber-timeline__bar timber-timeline__bar--info" :style="{ height: barHeight(bucket.info) }"></div>
                <div v-if="bucket.debug" class="timber-timeline__bar timber-timeline__bar--debug" :style="{ height: barHeight(bucket.debug) }"></div>
              </div>
              <span v-if="idx % labelStep === 0" class="timber-timeline__label">{{ bucket.label }}</span>
            </div>
          </div>
          <div v-if="tooltip.visible" class="timber-tooltip" :style="{ top: tooltip.y + 'px', left: tooltip.x + 'px' }">
            <div class="timber-tooltip__title">{{ tooltip.label }}</div>
            <div class="timber-tooltip__row" v-if="tooltip.info"><span class="legend-dot legend-dot--info"></span>Info: <strong>{{ tooltip.info }}</strong></div>
            <div class="timber-tooltip__row" v-if="tooltip.warn"><span class="legend-dot legend-dot--warn"></span>Warn: <strong>{{ tooltip.warn }}</strong></div>
            <div class="timber-tooltip__row" v-if="tooltip.error"><span class="legend-dot legend-dot--error"></span>Error: <strong>{{ tooltip.error }}</strong></div>
            <div class="timber-tooltip__row" v-if="tooltip.debug"><span class="legend-dot legend-dot--debug"></span>Debug: <strong>{{ tooltip.debug }}</strong></div>
            <div class="timber-tooltip__row timber-tooltip__row--total">Total: <strong>{{ tooltip.total }}</strong></div>
          </div>
        </div>
      </div>
    </div>

    <div v-else-if="!loading && events.length === 0" class="timber-empty">
      <span>No events in the last {{ selectedDays === 1 ? '24 hours' : selectedDays + ' days' }}</span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TimberStatistics',
  props: {
    periods: {
      type: Array,
      default: function () { return [1, 7, 14, 30] }
    }
  },
  data: function () {
    return {
      selectedDays: 7,
      loading: false,
      events: [],
      usage: {},
      tooltip: {
        visible: false,
        x: 0,
        y: 0,
        label: '',
        info: 0,
        warn: 0,
        error: 0,
        debug: 0,
        total: 0
      }
    }
  },
  computed: {
    totalEvents: function () {
      return this.events.length
    },
    errorCount: function () {
      return this.events.filter(function (e) { return e.level === 'error' }).length
    },
    warnCount: function () {
      return this.events.filter(function (e) { return e.level === 'warn' }).length
    },
    errorRate: function () {
      if (!this.events.length) return 0
      return Math.round((this.errorCount / this.events.length) * 100)
    },
    warnRate: function () {
      if (!this.events.length) return 0
      return Math.round((this.warnCount / this.events.length) * 100)
    },
    timelineBuckets: function () {
      if (!this.events.length) return []
      var sorted = this.events.slice().sort(function (a, b) {
        return new Date(a.timestamp) - new Date(b.timestamp)
      })
      var first = new Date(sorted[0].timestamp)
      var last = new Date(sorted[sorted.length - 1].timestamp)
      var spanMs = last - first
      var bucketMs, fmt
      if (spanMs <= 3600000) {
        bucketMs = 300000
        fmt = 'minute'
      } else if (spanMs <= 86400000) {
        bucketMs = 3600000
        fmt = 'hour'
      } else {
        bucketMs = 86400000
        fmt = 'day'
      }
      var startT = Math.floor(first.getTime() / bucketMs) * bucketMs
      var endT = Math.floor(last.getTime() / bucketMs) * bucketMs + bucketMs
      var self = this
      var buckets = []
      for (var t = startT; t < endT; t += bucketMs) {
        buckets.push({ t: t, info: 0, warn: 0, error: 0, debug: 0, label: self.bucketLabel(t, fmt) })
      }
      sorted.forEach(function (e) {
        var eT = new Date(e.timestamp).getTime()
        var idx = Math.floor((eT - startT) / bucketMs)
        if (idx >= 0 && idx < buckets.length) {
          var lvl = e.level || 'info'
          if (buckets[idx][lvl] !== undefined) buckets[idx][lvl]++
        }
      })
      return buckets
    },
    timelineMax: function () {
      var max = 0
      this.timelineBuckets.forEach(function (b) {
        var total = b.info + b.warn + b.error + b.debug
        if (total > max) max = total
      })
      return Math.max(max, 1)
    },
    labelStep: function () {
      var n = this.timelineBuckets.length
      if (n > 24) return Math.ceil(n / 8)
      if (n > 12) return 2
      return 1
    }
  },
  mounted: function () {
    this.loadData()
    this.loadUsage()
  },
  methods: {
    changePeriod: function (days) {
      this.selectedDays = days
      this.loadData()
    },
    loadData: function () {
      var self = this
      self.loading = true
      var from = new Date()
      from.setDate(from.getDate() - self.selectedDays)
      self.$integrationApi.get('/timber/logs', {
        params: {
          limit: 200,
          from: from.toISOString()
        }
      }).then(function (resp) {
        self.events = resp.data && resp.data.data ? resp.data.data : []
      }).catch(function () {
        self.events = []
      }).finally(function () {
        self.loading = false
      })
    },
    loadUsage: function () {
      var self = this
      self.$integrationApi.get('/timber/usage').then(function (resp) {
        self.usage = resp.data || {}
      }).catch(function () {
        self.usage = { tier: 'free', bytes_ingested: 0 }
      })
    },
    formatBytes: function (bytes) {
      if (!bytes) return '0 B'
      var units = ['B', 'KB', 'MB', 'GB']
      var size = Number(bytes)
      var unit = 0
      while (size >= 1024 && unit < units.length - 1) {
        size = size / 1024
        unit++
      }
      return size.toFixed(unit === 0 ? 0 : 1) + ' ' + units[unit]
    },
    bucketLabel: function (timestamp, fmt) {
      var d = new Date(timestamp)
      if (fmt === 'minute') return d.getHours() + ':' + String(d.getMinutes()).padStart(2, '0')
      if (fmt === 'hour') return d.getHours() + ':00'
      return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
    },
    barHeight: function (count) {
      if (!this.timelineMax) return '0%'
      return Math.max((count / this.timelineMax) * 100, 2) + '%'
    },
    showTooltip: function (bucket, event) {
      this.tooltip.visible = true
      this.tooltip.label = bucket.label
      this.tooltip.info = bucket.info
      this.tooltip.warn = bucket.warn
      this.tooltip.error = bucket.error
      this.tooltip.debug = bucket.debug
      this.tooltip.total = bucket.info + bucket.warn + bucket.error + bucket.debug
      this.moveTooltip(event)
    },
    moveTooltip: function (event) {
      var x = event.clientX + 12
      var y = event.clientY - 100
      if (x + 180 > window.innerWidth) x = event.clientX - 190
      if (y < 4) y = event.clientY + 12
      this.tooltip.x = x
      this.tooltip.y = y
    },
    hideTooltip: function () {
      this.tooltip.visible = false
    }
  }
}
</script>

<style scoped>
.timber-statistics {
  width: 100%;
}

.statistics-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.statistics-header__left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.statistics-header__left h4 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
}

.period-tabs {
  display: flex;
  gap: 8px;
  background-color: var(--form-bg);
  padding: 4px;
  border-radius: 8px;
  border: 1px solid var(--border-default);
}

.period-tab {
  padding: 8px 16px;
  border: 1px solid var(--border-default);
  background: var(--form-bg);
  color: #9ca3af;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  transition: all 0.2s ease;
}

.period-tab:hover {
  background: var(--bg-card);
  border-color: var(--border-hover);
  color: var(--text-primary);
}

.period-tab.active {
  background: var(--primary-start);
  border-color: var(--primary-start);
  color: var(--text-primary);
}

/* KPI Cards */
.kpi-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
}

.kpi-card {
  background: var(--bg-card);
  border: 1px solid var(--border-default);
  border-radius: 8px;
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  transition: border-color 0.2s ease;
}

.kpi-card:hover {
  border-color: var(--border-hover);
}

.kpi-card--error {
  border-left: 3px solid #f87171;
}

.kpi-card--warn {
  border-left: 3px solid #fbbf24;
}

.kpi-icon {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  flex-shrink: 0;
}

.kpi-icon--blue {
  background: rgba(129, 140, 248, 0.2);
  color: #818cf8;
}

.kpi-icon--red {
  background: rgba(248, 113, 113, 0.2);
  color: #f87171;
}

.kpi-icon--yellow {
  background: rgba(251, 191, 36, 0.2);
  color: #fbbf24;
}

.kpi-icon--teal {
  background: rgba(79, 209, 199, 0.2);
  color: #4fd1c7;
}

.kpi-details {
  flex: 1;
}

.kpi-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #9ca3af;
  font-weight: 600;
  margin-bottom: 4px;
}

.kpi-value {
  font-size: 24px;
  font-weight: 700;
  color: var(--text-primary);
  line-height: 1;
}

.kpi-sub {
  font-size: 11px;
  color: var(--text-muted);
  margin-top: 2px;
}

/* Chart */
.timber-chart {
  background: var(--bg-card);
  border: 1px solid var(--border-default);
  border-radius: 8px;
  padding: 18px;
}

.timber-chart__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.timber-chart__header h5 {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
}

.timber-chart__legend {
  display: flex;
  gap: 12px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  color: var(--text-muted);
}

.legend-dot {
  width: 8px;
  height: 8px;
  border-radius: 2px;
  flex-shrink: 0;
}

.legend-dot--info { background: #818cf8; }
.legend-dot--warn { background: #fbbf24; }
.legend-dot--error { background: #f87171; }
.legend-dot--debug { background: #64748b; }

/* Timeline */
.timber-timeline {
  display: flex;
  gap: 6px;
  height: 180px;
}

.timber-timeline__y-axis {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 30px;
  padding-bottom: 22px;
}

.timber-timeline__y-label {
  font-size: 10px;
  color: var(--text-muted);
  text-align: right;
  line-height: 1;
}

.timber-timeline__area {
  flex: 1;
  position: relative;
}

.timber-timeline__grid {
  position: absolute;
  inset: 0;
  bottom: 22px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  pointer-events: none;
}

.timber-timeline__grid-line {
  border-bottom: 1px solid var(--border-separator);
}

.timber-timeline__bars {
  position: absolute;
  inset: 0;
  display: flex;
  gap: 3px;
  padding: 0 2px;
}

.timber-timeline__col {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 0;
  cursor: pointer;
}

.timber-timeline__stack {
  flex: 1 1 auto;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  padding-bottom: 0;
}

.timber-timeline__bar {
  width: 80%;
  max-width: 24px;
  min-height: 2px;
  border-radius: 2px 2px 0 0;
  transition: opacity 0.15s;
}

.timber-timeline__col:hover .timber-timeline__bar {
  opacity: 0.8;
}

.timber-timeline__bar--info { background: #818cf8; }
.timber-timeline__bar--warn { background: #fbbf24; }
.timber-timeline__bar--error { background: #f87171; }
.timber-timeline__bar--debug { background: #64748b; }

.timber-timeline__label {
  font-size: 10px;
  color: var(--text-muted);
  white-space: nowrap;
  margin-top: 4px;
  flex-shrink: 0;
  height: 18px;
}

/* Tooltip */
.timber-tooltip {
  position: fixed;
  z-index: 9999;
  background: #1e2433;
  border: 1px solid var(--border-default);
  border-radius: 8px;
  padding: 10px 14px;
  pointer-events: none;
  min-width: 150px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
}

.timber-tooltip__title {
  font-size: 12px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 6px;
}

.timber-tooltip__row {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: var(--text-secondary);
  margin-top: 3px;
}

.timber-tooltip__row--total {
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px solid var(--border-separator);
  color: var(--text-primary);
}

/* Empty */
.timber-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: var(--text-muted);
  font-size: 13px;
  background: var(--bg-card);
  border: 1px solid var(--border-default);
  border-radius: 8px;
}

@media (max-width: 768px) {
  .statistics-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .kpi-cards {
    grid-template-columns: repeat(2, 1fr);
  }

  .timber-chart__header {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
}
</style>
